/**
 * Fetches Open Graph metadata (image + title) from a product URL.
 * Primary: server-side Cloud Function (no CORS issues, proper User-Agent).
 * Fallback: client-side CORS proxies.
 */

// Cloud Function URL — set via env var
function getCloudFunctionUrl() {
  return process.env.REACT_APP_METADATA_FUNCTION_URL || null;
}

const CORS_PROXIES = [
  (url) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
  (url) => `https://corsproxy.io/?url=${encodeURIComponent(url)}`,
  (url) => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`,
];
const FETCH_TIMEOUT_MS = 6000;

/**
 * Fetch metadata via the server-side Cloud Function.
 * Returns { imageUrl, title } or null if unavailable.
 */
async function fetchViaCloudFunction(sourceUrl) {
  const cloudFunctionUrl = getCloudFunctionUrl();
  if (!cloudFunctionUrl) return null;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const response = await fetch(
      `${cloudFunctionUrl}?url=${encodeURIComponent(sourceUrl)}`,
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);

    if (!response.ok) return null;

    const data = await response.json();
    if (data && (data.imageUrl || data.title)) {
      return data;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetch HTML from the source URL, trying multiple CORS proxies in order.
 * @param {string} sourceUrl - The full product URL to fetch
 * @returns {Promise<string|null>} Raw HTML string, or null on failure
 */
async function fetchHtmlViaProxy(sourceUrl) {
  for (const buildProxyUrl of CORS_PROXIES) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

      const response = await fetch(buildProxyUrl(sourceUrl), {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) continue;

      const html = await response.text();
      if (html && html.length > 200) return html;
    } catch {
      // Try the next proxy
    }
  }
  return null;
}

/**
 * Fetch metadata (OG image + page title) from a URL.
 * Tries Cloud Function first, then falls back to CORS proxies.
 * @param {string} sourceUrl - The full product URL to fetch
 * @returns {Promise<{ imageUrl: string|null, title: string|null }>}
 */
export async function fetchPageMetadata(sourceUrl) {
  if (!sourceUrl || typeof sourceUrl !== 'string') {
    return { imageUrl: null, title: null };
  }

  // Try server-side Cloud Function first (most reliable)
  const cloudResult = await fetchViaCloudFunction(sourceUrl);
  if (cloudResult) return cloudResult;

  // Fall back to client-side CORS proxies
  const html = await fetchHtmlViaProxy(sourceUrl);
  if (!html) return { imageUrl: null, title: null };

  let imageUrl = extractOgImage(html);
  let title = extractPageTitle(html);

  // Fall back to JSON-LD structured data (common on e-commerce sites)
  if (!imageUrl || !title) {
    const jsonLd = extractJsonLd(html);
    if (!imageUrl && jsonLd.imageUrl) imageUrl = jsonLd.imageUrl;
    if (!title && jsonLd.title) title = jsonLd.title;
  }

  return { imageUrl, title };
}

/**
 * Extract the og:image (or fallback meta image) from a URL's HTML.
 * @param {string} sourceUrl - The full product URL to fetch
 * @returns {Promise<string|null>} The image URL, or null on failure
 * @deprecated Use fetchPageMetadata() instead for both image and title
 */
export async function fetchOgImage(sourceUrl) {
  const { imageUrl } = await fetchPageMetadata(sourceUrl);
  return imageUrl;
}

/**
 * Extract product name and image from JSON-LD structured data.
 * Many e-commerce sites embed Product schema even when OG tags are missing.
 * @param {string} html - Raw HTML string
 * @returns {{ imageUrl: string|null, title: string|null }}
 */
export function extractJsonLd(html) {
  if (!html) return { imageUrl: null, title: null };

  const result = { imageUrl: null, title: null };
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const scripts = doc.querySelectorAll('script[type="application/ld+json"]');

  for (const script of scripts) {
    try {
      const data = JSON.parse(script.textContent);
      const product = findJsonLdProduct(data);
      if (product) {
        if (!result.title && product.name) {
          result.title = product.name;
        }
        if (!result.imageUrl && product.image) {
          const img = Array.isArray(product.image)
            ? product.image[0]
            : product.image;
          const imgUrl = typeof img === 'string' ? img : img?.url || img?.contentUrl;
          if (imgUrl && imgUrl.startsWith('http')) {
            result.imageUrl = imgUrl;
          }
        }
        if (result.title && result.imageUrl) break;
      }
    } catch {
      // Invalid JSON — skip
    }
  }

  return result;
}

function findJsonLdProduct(data) {
  if (!data || typeof data !== 'object') return null;

  const productTypes = ['Product', 'IndividualProduct'];

  if (data['@type']) {
    const types = Array.isArray(data['@type']) ? data['@type'] : [data['@type']];
    if (types.some(t => productTypes.includes(t))) return data;
  }

  if (Array.isArray(data['@graph'])) {
    for (const item of data['@graph']) {
      const found = findJsonLdProduct(item);
      if (found) return found;
    }
  }

  if (Array.isArray(data)) {
    for (const item of data) {
      const found = findJsonLdProduct(item);
      if (found) return found;
    }
  }

  return null;
}

/**
 * Parse HTML and extract the best available meta image URL.
 * Checks og:image, og:image:secure_url, and twitter:image in order.
 * @param {string} html - Raw HTML string
 * @returns {string|null} The image URL, or null if not found
 */
export function extractOgImage(html) {
  if (!html) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Try meta tags in priority order
  const selectors = [
    'meta[property="og:image"]',
    'meta[property="og:image:secure_url"]',
    'meta[name="twitter:image"]',
  ];

  for (const selector of selectors) {
    const meta = doc.querySelector(selector);
    const content = meta?.getAttribute('content');
    if (content && content.startsWith('http')) {
      return content;
    }
  }

  return null;
}

/**
 * Parse HTML and extract the page title from meta tags or <title>.
 * Checks og:title, twitter:title, and <title> in priority order.
 * @param {string} html - Raw HTML string
 * @returns {string|null} The page title, or null if not found
 */
export function extractPageTitle(html) {
  if (!html) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const selectors = [
    'meta[property="og:title"]',
    'meta[name="twitter:title"]',
  ];

  for (const selector of selectors) {
    const meta = doc.querySelector(selector);
    const content = meta?.getAttribute('content')?.trim();
    if (content) return content;
  }

  // Fall back to <title> tag
  const titleEl = doc.querySelector('title');
  const titleText = titleEl?.textContent?.trim();
  if (titleText) return titleText;

  return null;
}
